import { unwrap } from '@sohanemon/utils';
import { getQueryKey } from '@trpc/react-query';
import { type ExternalToast, toast } from 'sonner';
import type { AppRouteTerminals } from '@/server/root';
import type {
	AlertContext,
	AlertOpts,
	RouteItem,
	RouteItemHandler,
	UpdateDefinition,
} from '@/types/global';
import { trpcProcedures } from '../generated/trpc-spec.gen';
import { api } from '../trpc/client';
import { queryClient } from './query-client';

function showAlert(
	type: AlertContext['type'],
	options: {
		message?: AlertOpts;
		extraOpts?: ExternalToast;
		context?: Omit<AlertContext, 'type'>;
	},
) {
	const { message, extraOpts, context } = options;
	if (!message) return;
	const fullContext = { type, ...context };
	const resolvedMessage = unwrap(message, fullContext);

	const { title, ...opts } =
		typeof resolvedMessage === 'string'
			? { title: resolvedMessage }
			: typeof resolvedMessage === 'object'
				? resolvedMessage
				: {};
	const finalOpts = extraOpts ? { ...opts, ...extraOpts } : opts;
	if (type === 'loading') {
		toast.loading(title, { id: title?.toString(), ...finalOpts });
	} else {
		toast[type](title, finalOpts);
	}
}

function getIsEnabled(queryKey: readonly unknown[]): boolean {
	// NOTE: tRpc only
	if (Array.isArray(queryKey.at(0))) {
		const route = (queryKey.at(0) as string[]).join('.');
		const isPublic = trpcProcedures[route as AppRouteTerminals]?.isPublic;
		if (isPublic) return true;

		if (typeof window === 'undefined') {
			// NOTE: in server, disable private routes (queries don't run on server anyway)
			return false;
		} else {
			return !!window.userId;
		}
	}
	return true;
}

function applyOptimisticUpdates(
	updates: UpdateDefinition<any, any> | UpdateDefinition<any, any>[],
	{
		variables,
		context,
		data = null,
	}: {
		variables: any;
		context?: any; // mutate only
		data?: any; // success only
	},
) {
	const list = Array.isArray(updates) ? updates : [updates];

	for (const upd of list) {
		const queryKey = getQueryKey(
			// @ts-expect-error route must be typed
			api[upd.route],
			upd.input,
			'query',
		);

		queryClient.setQueryData(queryKey, (prevData: unknown) => {
			if (!data) {
				// NOTE: only required for mutation, success must have data
				context.prevData = context.prevData || {};
				context.prevData[upd.route] = prevData;
			}

			return upd.updater({ prevData, variables, data });
		});
	}

	return context;
}

function runRouteOperations({
	routes,
	op,
	data,
	variables,
}: {
	routes: Partial<RouteItem>[] | RouteItemHandler | undefined;
	op: 'refetch' | 'remove' | 'invalidate';
} & Parameters<RouteItemHandler>[0]) {
	if (!routes) return;

	const routeList = unwrap(routes, { data, variables });
	if (!Array.isArray(routeList) || routeList.length === 0) return;

	for (const item of routeList) {
		try {
			const routeName = typeof item === 'string' ? item : item.route;
			const input = typeof item === 'string' ? undefined : item.input;

			if (!routeName) continue;

			const proc = (api as any)[routeName];
			if (!proc) continue;

			const queryKey =
				input === undefined
					? getQueryKey(proc)
					: getQueryKey(proc, input, 'query');

			if (!queryKey) continue;

			switch (op) {
				case 'refetch':
					queryClient.refetchQueries({ queryKey });
					break;
				case 'remove':
					queryClient.removeQueries({ queryKey });
					break;
				case 'invalidate':
					queryClient.invalidateQueries({ queryKey });
					break;
			}
		} catch (err) {
			console.info('⚡[query-client runRouteOperations]:', err);
		}
	}
}

export default {
	applyOptimisticUpdates,
	getIsEnabled,
	runRouteOperations,
	showAlert,
};
